/**
 * Helpers for LLM-based CAP screening configs.
 *
 * What screens for a template is declared on the template itself:
 * screening_kind is "classifier" (a packaged model, chosen per config) or
 * "llm" (the config's screening persona supplies the model and the
 * instructions, so an llm config carries no model_version at all). The kind
 * is fixed at template creation. This is the single detection signal used
 * across the CAP automation UI.
 *
 * Before the backend declared the kind, a config was "LLM-backed" when its
 * model version was a remote text_generative LLM. That signal is kept only
 * as the fallback when neither the template nor the config carries a
 * screening_kind (an older backend, or a template not loaded yet); the kind
 * migration backfills such templates to "llm".
 */

export const SCREENING_KIND = Object.freeze({
  CLASSIFIER: "classifier",
  LLM: "llm",
});

export const isLlmModelVersion = (modelVersion) =>
  modelVersion?.output_data_config?.output_type === "text_generative";

/** True when the template declares LLM screening. */
export const isLlmTemplate = (template) =>
  template?.screening_kind === SCREENING_KIND.LLM;

// The kind is read off the template, or off the config itself when the
// backend returns it there (AutomationConfig.screening_kind). With neither,
// the fallback is the kind migration's own definition of an LLM config: a
// text_generative model version or saved LLM settings, plus an explicitly
// null model version (a classifier config always carries one).
export const isLlmAutomationConfig = (automationConfig, template) => {
  const kind = template?.screening_kind || automationConfig?.screening_kind;
  if (kind) return kind === SCREENING_KIND.LLM;
  if (!automationConfig) return false;
  return (
    automationConfig.model_version === null ||
    isLlmModelVersion(automationConfig.model_version) ||
    Boolean(automationConfig.llm_screening_config)
  );
};

/**
 * Display text for the model version a config screens with, branching on
 * the config's screening kind as the caller resolved it (CapAuto passes
 * isLlm only while the LLM screening gate is open). An llm config has no
 * model_version (its persona owns the model); any other config shows its
 * version number exactly as before, blank when it has none.
 */
export const configModelVersionLabel = (
  automationConfig,
  { isLlm = false } = {},
) =>
  isLlm
    ? "LLM (screening persona)"
    : automationConfig?.model_version?.version_number;

export const DEFAULT_LLM_INSTRUCTIONS = `You are a CAP screening analyst at a nuclear power plant. Consult the CAP screening procedures dataset before deciding, and base every decision on the specific procedure criteria. Cite the procedure sections you relied on in each field's references. If the condition report is ambiguous or the procedures do not clearly apply, lower your confidence accordingly.`;

/**
 * A model is "LLM-backed" when every one of its versions is a text-generative
 * remote LLM. A model with any classical version stays classical (it is still
 * usable without the LLM screening module); a model with no versions is not
 * an LLM either — it cannot be configured for LLM screening.
 */
export const isLlmModel = (model) =>
  Array.isArray(model?.versions) &&
  model.versions.length > 0 &&
  model.versions.every(isLlmModelVersion);
